import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FaHome, 
  FaSearch, 
  FaPlus, 
  FaUser, 
  FaComments,
  FaBell,
  FaInfoCircle,
  FaQuestionCircle,
  FaBook,
  FaCog,
  FaBars,
  FaTimes,
  FaChevronDown
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const AppBarContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #111;
  border-bottom: 1px solid #333;
  backdrop-filter: blur(10px);
`;

const AppBarContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;

  @media (max-width: 768px) {
    padding: 0 16px;
    height: 56px;
  }
`;

const Logo = styled.div`
  color: #4CAF50;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #45a049;
  }

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.active ? '#4CAF50' : '#ccc'};
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background-color: #222;
    color: ${props => props.active ? '#4CAF50' : '#fff'};
  }

  svg {
    font-size: 16px;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled(NavButton)`
  &::after {
    content: '';
    display: inline-block;
    margin-left: 4px;
    transition: transform 0.2s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #222;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 8px 0;
  min-width: 200px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1001;
`;

const DropdownItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: #ccc;
  padding: 12px 16px;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #333;
    color: #fff;
  }

  svg {
    font-size: 16px;
    width: 16px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #ccc;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #222;
    color: #fff;
  }

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1002;
  opacity: ${props => props.isOpen ? '1' : '0'};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
`;

const MobileMenuContent = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 280px;
  height: 100%;
  background-color: #111;
  border-left: 1px solid #333;
  padding: 20px;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  overflow-y: auto;
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #333;
`;

const MobileMenuTitle = styled.div`
  color: #4CAF50;
  font-size: 20px;
  font-weight: bold;
`;

const MobileMenuClose = styled.button`
  background: none;
  border: none;
  color: #ccc;
  font-size: 20px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #222;
    color: #fff;
  }
`;

const MobileNavSection = styled.div`
  margin-bottom: 24px;
`;

const MobileNavSectionTitle = styled.div`
  color: #666;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
  padding: 0 8px;
`;

const MobileNavItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: ${props => props.active ? '#4CAF50' : '#ccc'};
  padding: 12px 8px;
  font-size: 16px;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  transition: all 0.2s ease;
  margin-bottom: 4px;

  &:hover {
    background-color: #222;
    color: ${props => props.active ? '#4CAF50' : '#fff'};
  }

  svg {
    font-size: 18px;
    width: 20px;
  }
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const AppBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const mainNavItems = [
    { path: '/feed', label: 'Feed', icon: FaHome },
    { path: '/search', label: 'Search', icon: FaSearch },
    { path: '/create', label: 'Create', icon: FaPlus },
    { path: '/chat', label: 'Chat', icon: FaComments },
    { path: '/notifications', label: 'Notifications', icon: FaBell },
  ];

  const pageItems = [
    { path: '/about', label: 'About', icon: FaInfoCircle },
    { path: '/help', label: 'Help & Support', icon: FaQuestionCircle },
    { path: '/terms', label: 'Terms of Service', icon: FaBook },
    { path: '/privacy', label: 'Privacy Policy', icon: FaBook },
    { path: '/settings', label: 'Settings', icon: FaCog },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <AppBarContainer>
      <AppBarContent>
        <Logo onClick={() => navigate('/feed')}>
          Weloop
        </Logo>

        <Navigation>
          {mainNavItems.map((item) => (
            <NavButton
              key={item.path}
              active={isActive(item.path)}
              onClick={() => handleNavClick(item.path)}
            >
              <item.icon />
              {item.label}
            </NavButton>
          ))}

          <DropdownContainer>
            <DropdownButton
              active={pageItems.some(item => isActive(item.path))}
              isOpen={isDropdownOpen}
              onClick={toggleDropdown}
            >
              <FaBars />
              Pages
              <FaChevronDown />
            </DropdownButton>
            <DropdownMenu isOpen={isDropdownOpen}>
              {pageItems.map((item) => (
                <DropdownItem
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                >
                  <item.icon />
                  {item.label}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </DropdownContainer>
        </Navigation>

        <UserSection>
          <UserAvatar
            src={user?.meta?.profile_image || '/default-avatar.png'}
            alt="Profile"
            onClick={() => navigate('/profile')}
          />
          <MobileMenuButton onClick={toggleMobileMenu}>
            <FaBars />
          </MobileMenuButton>
        </UserSection>
      </AppBarContent>

      <MobileMenu isOpen={isMobileMenuOpen}>
        <MobileMenuContent isOpen={isMobileMenuOpen}>
          <MobileMenuHeader>
            <MobileMenuTitle>Menu</MobileMenuTitle>
            <MobileMenuClose onClick={toggleMobileMenu}>
              <FaTimes />
            </MobileMenuClose>
          </MobileMenuHeader>

          <MobileNavSection>
            <MobileNavSectionTitle>Main Navigation</MobileNavSectionTitle>
            {mainNavItems.map((item) => (
              <MobileNavItem
                key={item.path}
                active={isActive(item.path)}
                onClick={() => handleNavClick(item.path)}
              >
                <item.icon />
                {item.label}
              </MobileNavItem>
            ))}
          </MobileNavSection>

          <MobileNavSection>
            <MobileNavSectionTitle>Pages</MobileNavSectionTitle>
            {pageItems.map((item) => (
              <MobileNavItem
                key={item.path}
                active={isActive(item.path)}
                onClick={() => handleNavClick(item.path)}
              >
                <item.icon />
                {item.label}
              </MobileNavItem>
            ))}
          </MobileNavSection>

          <MobileNavSection>
            <MobileNavSectionTitle>Profile</MobileNavSectionTitle>
            <MobileNavItem
              active={isActive('/profile')}
              onClick={() => handleNavClick('/profile')}
            >
              <FaUser />
              Profile
            </MobileNavItem>
          </MobileNavSection>
        </MobileMenuContent>
      </MobileMenu>
    </AppBarContainer>
  );
};

export default AppBar; 